import { DropEvent } from "react-dropzone";
import JSZip from "jszip";

/**
 * Extract the files from a {@linkcode DropEvent} regardless of whether it's a drag or change event, and regardless of
 * whether the files are provided as a FileList or as an array of DataTransferItems.
 */
const extractFiles = (event: DropEvent): File[] => {
  if ("dataTransfer" in event) {
    return Array.from(event.dataTransfer?.files ?? []);
  } else if ("target" in event && event.target && "files" in event.target) {
    return Array.from(event.target?.files ?? []);
  }
  return [];
};

/**
 * Custom function to handle the file drop event within DropZone
 *
 * This function will check if the file is a zip file and if so, it will extract the files from the zip
 * and return them as an array of files. If the file is not a zip file, it will return the file as is.
 */
export const getFilesFromEvent = async (event: DropEvent): Promise<File[]> => {
  const files = [];
  const fileList: File[] = extractFiles(event);
  for (const file of fileList) {
    if (["application/zip", "application/x-zip-compressed"].includes(file.type)) {
      const zip = await JSZip.loadAsync(file);
      const zipFiles = await Promise.all(
        Object.keys(zip.files).map(async (filename) => {
          const zipFile = zip.files[filename];
          const blob = await zipFile.async("blob");
          return new File([blob], filename);
        })
      );
      files.push(...zipFiles);
    } else {
      files.push(file);
    }
  }

  return files;
};

/**
 * Will remove the leading path component(s) from a list of files, effectively flattening the folder structure.
 *
 * If a zip file with a nested folder structure is uploaded, we may need to flatten it to get our desired structure.
 *
 * @param files - The list of files to flatten
 * @param level - The number of leading path components to remove. Default is {@linkcode Infinity}, which will remove all leading path components.
 */
export const flattenFolderStructureFromFileList = (files: File[], level: number = Infinity): File[] => {
  return files.map((file) => {
    // Check if the file has a relative path (indicating it's from a folder structure)
    if (file.name.includes("/")) {
      const parts = file.name.split("/");
      const maxLevel = Math.min(level, parts.length - 1);
      // Create a new File object with the name stripped of the leading path components up to the specified level
      return new File([file], parts.slice(maxLevel).join("/"));
    }
    return file;
  });
};

/**
 * The files from a {@linkcode DropEvent}, with any zip archive expanded and its folder structure flattened.
 */
export const getFlattenedFilesFromEvent = async (event: DropEvent): Promise<File[]> =>
  flattenFolderStructureFromFileList(await getFilesFromEvent(event));
